import { useTranslation } from 'react-i18next';

import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { cn } from '~/utils/cn';

import type { ArchiveView } from '../types/list-archive.types';

interface ArchiveViewSwitchProps {
  value: ArchiveView;
  onChange: (view: ArchiveView) => void;
  /** Archived-row count for the badge; omit while it is still loading. */
  archivedCount?: number | undefined;
  className?: string;
  /** `data-testid` for the switch; each option gets `${testId}-active` / `${testId}-archived`. */
  testId?: string;
}

/**
 * Admin-only `Active | Archived` switch rendered on each list page. Orthogonal to
 * the page's status tabs — in the Archived view every list / count request adds
 * `archived=ARCHIVED` and the status tabs keep filtering inside that set.
 * Sits on card surfaces, so it keeps SegmentedTabs' bordered `bg-background` look.
 */
export function ArchiveViewSwitch({
  value,
  onChange,
  archivedCount,
  className,
  testId = 'archive-view-switch',
}: ArchiveViewSwitchProps) {
  const { t } = useTranslation('common');
  return (
    <SegmentedTabs<ArchiveView>
      value={value}
      onChange={onChange}
      ariaLabel={t('listArchive.view.ariaLabel')}
      testId={testId}
      // Compact control: override the component's full-width default so it hugs
      // its two labels instead of stretching across the toolbar.
      className={cn('w-auto shrink-0 sm:max-w-none', className)}
      options={[
        { value: 'active', label: t('listArchive.view.active') },
        {
          value: 'archived',
          label: t('listArchive.view.archived'),
          ...(archivedCount !== undefined ? { badge: archivedCount.toLocaleString() } : {}),
        },
      ]}
    />
  );
}
